import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import TaskPopup from './TaskPopup';
import { getTasks, updateTask } from './apiService';
import moment from 'moment';
import 'moment-timezone';
import axios from 'axios';
import EventRenderer from './EventRenderer'; // Your beautiful EventRenderer
import thLocale from '@fullcalendar/core/locales/th';
import { useTranslation } from 'react-i18next';
import './Calendar.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';


const Calendar = ({ onTasksChange, onClassesChange }) => {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [classes, setClasses] = useState([]);
  const [isTaskPopupOpen, setIsTaskPopupOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [dateSelected, setDateSelected] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchTasks();
    fetchClasses();
    fetchUsersWithRoles();
  }, []);

  // Same palette AllClasses uses, so both calendars read identically.
  const getUnifiedColorScheme = (platform, branchId) => {
    if (platform === 'online') {
      return { backgroundColor: '#E6F4FF', borderColor: '#1890ff', textColor: '#1890ff' };
    }
    if (platform === 'onsite' && Number(branchId) === 1) {
      return { backgroundColor: 'transparent', borderColor: '#000000', textColor: '#000000' };
    }
    if (platform === 'onsite' && Number(branchId) === 2) {
      return { backgroundColor: '#FFFFE0', borderColor: '#DAA520', textColor: '#000000' };
    }
    return { backgroundColor: '#f0f0f0', borderColor: '#999999', textColor: '#333333' };
  };

    const formatScheduleHour = (scheduleHour) => {
    if (!scheduleHour) return null;
    
    // Handle different possible formats
    const str = scheduleHour.toString();
    
    // If it's already in HH:MM format, return as is
    if (/^\d{1,2}:\d{2}$/.test(str)) {
      return str;
    }
    
    // Parse "00h 00m" format
    const hourMatch = str.match(/(\d+)h/);
    const minuteMatch = str.match(/(\d+)m/);
    
    const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
    
    // Format as HH:MM
    return `${hours.toString().padStart(1, '0')}.${minutes.toString().padStart(2, '0')} hr`;
  };

  // *** FIXED: Added editable properties to tasks ***
  const fetchTasks = async () => {
    try {
      const fetchedTasks = await getTasks();
      const transformedTasks = fetchedTasks.map(task => ({
        id: `task-${task.eventid.toString()}`,
        title: task.title,
        start: moment(task.start).local().toISOString(),
        end: moment(task.end).local().toISOString(),
        description: task.description,
        type: task.shared ? 'shared-task' : 'task',
        shared: task.shared,
        schedule_hour: moment(task.end).diff(moment(task.start), 'minutes'),
        // The event card draws its own background, so carry the colours here
        // instead of leaning on the .task-event / .shared-event CSS.
        backgroundColor: task.shared ? '#F0F0FA' : '#FFF1F0',
        borderColor: task.shared ? '#515c96' : '#da251c',
        textColor: task.shared ? '#2f3663' : '#a8071a',
        // *** CRITICAL: Add these properties for drag and drop ***
        editable: true,
        startEditable: true,
        durationEditable: true
      }));
      setTasks(transformedTasks);
      onTasksChange(transformedTasks);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    }
  };
  
  // *** FIXED: Added explicit non-editable properties to classes ***
  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes/teacher`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      const transformedClasses = response.data.map((cls) => {
        const startDate = moment(cls.date).format('YYYY-MM-DD') + 'T' + cls.schedule_time;
        const endDate = moment(cls.date).format('YYYY-MM-DD') + 'T' + cls.end_time;

        const titleParts = [
          cls.class_code,
          cls.subject_name,
          cls.classroom_number && cls.classroom_number !== 'null' ? cls.classroom_number : null,
        ].filter(Boolean);

        // Platform/branch palette first, then let attendance override it.
        // Priority: Absent > To be rescheduled — both flag a problem, Absent is
        // solid red and rescheduled is outlined red. Matches AllClasses.
        const attendanceRecords = cls.attendance_statuses || [];
        const hasAbsent = attendanceRecords.some((a) => a.status === 'Absent');
        const hasToBeRescheduled = attendanceRecords.some((a) => a.status === 'To be rescheduled');

        let { backgroundColor, borderColor, textColor } = getUnifiedColorScheme(
          cls.platform,
          cls.branch_id
        );
        if (hasAbsent) {
          backgroundColor = '#ff4d4f';
          borderColor = '#ff4d4f';
          textColor = '#ffffff';
        } else if (hasToBeRescheduled) {
          backgroundColor = '#ffffff';
          borderColor = '#ff4d4f';
          textColor = '#cf1322';
        }

        return {
          id: `class-${cls.classid}`,
          title: titleParts.join(' - '),
          start: startDate,
          end: endDate,
          type: 'class',
          // *** CRITICAL: Classes should NOT be editable ***
          editable: false,
          startEditable: false,
          durationEditable: false,
          color: borderColor,
          backgroundColor,
          borderColor,
          textColor,
          // Store detailed info for EventRenderer
          extendedProps: {
            classCode: cls.class_code,
            branchName: cls.branch_name,
            studentConfirm: cls.student_confirm === true || cls.student_confirm === 'true',
            subjectName: cls.subject_name,
            classroomNumber: cls.classroom_number && cls.classroom_number !== 'null' ? cls.classroom_number : null,
            platform: cls.platform && cls.platform !== 'null' ? cls.platform : null,
            teacherName: cls.teacher_name,
            scheduleHour: cls.schedule_hour,
            scheduleDay: cls.schedule_day,
            type: 'class',
            attendance_statuses: attendanceRecords,
          }
        };
      });

      setClasses(transformedClasses);
      onClassesChange(transformedClasses);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };
  
  const fetchUsersWithRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      const filteredUsers = response.data.filter((user) =>
        ['superadmin', 'admin', 'teacher'].includes(user.role)
      );
  
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users with roles:', error);
    }
  };
  
  const handleDateClick = (arg) => {
    const newStart = moment(arg.start);
    const newEnd = moment(arg.end);
  
    if (!newStart.isSame(newEnd, 'day')) {
      confirmAlert({
        title: t('Invalid Time Range'),
        message: t('Tasks cannot span multiple days. Please select a time range within the same day.'),
        buttons: [
          {
            label: t('Ok'),
            onClick: () => {},
          },
        ],
      });
      return;
    }
  
    const isOverlapping = classes.some((cls) => {
      const classStart = moment(cls.start);
      const classEnd = moment(cls.end);
  
      return (
        newStart.isBetween(classStart, classEnd, null, '[)') ||
        newEnd.isBetween(classStart, classEnd, null, '(]') ||
        (newStart.isBefore(classStart) && newEnd.isAfter(classEnd))
      );
    });
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class. Please choose a different time.'),
        buttons: [
          {
            label: t('Ok'),
            onClick: () => {},
          },
        ],
      });
      return;
    }
  
    if (arg.view.type !== 'dayGridMonth') {
      setDateSelected({ start: arg.start, end: arg.end });
      setSelectedTask(null);
      setIsTaskPopupOpen(true);
    }
  };
  
const handleEventClick = ({ event }) => {
  if (event.id.startsWith('class-')) {
    const nestedProps = event.extendedProps || {};
    const branchName = nestedProps?.branch_name;
    const classCode = nestedProps?.classCode || nestedProps?.class_code || event.title?.split(' - ')[0] || 'Class';
    const subjectName = nestedProps?.subjectName || nestedProps?.subject_name || event.title?.split(' - ')[1] || '';
    const classroomNumber = nestedProps?.classroomNumber || nestedProps?.classroom_number;
    const platform = nestedProps?.platform;
    const teacherName = nestedProps?.teacherName || nestedProps?.teacher_name;
    const scheduleHour = nestedProps?.scheduleHour || nestedProps?.schedule_hour;
    
    // Format start and end times
    const startTime = moment(event.start).format('HH:mm');
    const endTime = event.end ? moment(event.end).format('HH:mm') : 'N/A';
    const date = moment(event.start).format('dddd, MMMM Do YYYY');
    
    // Create detailed class information message
    const classDetails = `

      ${branchName}
      ${classCode}
      ${subjectName}
      ${date}
      ${startTime} - ${endTime}
      ${scheduleHour ? `${t('Hours')}: ${formatScheduleHour(scheduleHour)}` : ''}
      ${classroomNumber ? `${t('Classroom')}: ${classroomNumber}` : ''}
      ${platform ? `${t('Platform')}: ${platform}` : ''}
    `.trim().split('\n').filter(line => line.trim()).join('\n');
    
    confirmAlert({
      title: t('Class Information'),
      message: classDetails,
      buttons: [
        { 
          label: t('Close'), 
          onClick: () => {} 
        }
      ],
      customUI: ({ title, message, onClose }) => (
        <div className="custom-confirm-alert">
          <div className="custom-confirm-alert-body">
            <h2>{title}</h2>
            <div style={{ 
              textAlign: 'left', 
              whiteSpace: 'pre-line', 
              margin: '20px 0',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              {message}
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button 
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1890ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('Close')}
              </button>
            </div>
          </div>
        </div>
      )
    });
    return;
  }
  
  // Handle task events (existing functionality)
  const taskToEdit = tasks.find(task => task.id === event.id);
  if (taskToEdit) {
    setSelectedTask(taskToEdit);
    setIsTaskPopupOpen(true);
  }
};

  // *** ENHANCED: Better debugging for eventDrop ***
  const handleEventDrop = (info) => {

    if (info.event.id.startsWith('class-')) {
      confirmAlert({
        title: t('Cannot Edit'),
        message: t('Class events cannot be edited from the calendar.'),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }]
      });
      return;
    }
    
    const newStart = moment(info.event.start);
    const newEnd = moment(info.event.end);
  
    if (!newStart.isSame(newEnd, 'day')) {
      confirmAlert({
        title: t('Invalid Time Range'),
        message: t('You cannot drag an event to span across multiple days.'),
        buttons: [
          {
            label: t('Ok'),
            onClick: () => info.revert(),
          },
        ],
      });
      return;
    }
  
    const isOverlapping = [...tasks, ...classes].some(event => {
      if (event.id === info.event.id) return false;
  
      const eventStart = moment(event.start);
      const eventEnd = moment(event.end);
  
      return (
        newStart.isBetween(eventStart, eventEnd, null, '[)') || 
        newEnd.isBetween(eventStart, eventEnd, null, '(]') || 
        (newStart.isBefore(eventStart) && newEnd.isAfter(eventEnd))
      );
    });
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class or event. Please choose a different time.'),
        buttons: [
          {
            label: t('Ok'),
            onClick: () => info.revert(),
          },
        ],
      });
      return;
    }
    confirmAlert({
      title: t('Confirm to submit'),
      message: t('Are you sure you want to change the event?'),
      buttons: [
        {
          label: t('Yes'),
          onClick: () => confirmEventChange(info),
        },
        {
          label: t('No'),
          onClick: () => info.revert(),
        },
      ],
    });
  };
  
  const handleEventResize = (info) => {
    if (info.event.id.startsWith('class-')) {
      confirmAlert({
        title: t('Cannot Edit'),
        message: t('Class events cannot be edited from the calendar.'),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }]
      });
      return;
    }
    
    const newStart = moment(info.event.start);
    const newEnd = moment(info.event.end);
    const eventId = info.event.id;
  
    const isOverlapping = [...tasks, ...classes].some(event => {
      if (event.id === eventId) return false;
  
      const eventStart = moment(event.start);
      const eventEnd = moment(event.end);
  
      return (
        newStart.isBetween(eventStart, eventEnd, null, '[)') || 
        newEnd.isBetween(eventStart, eventEnd, null, '(]') || 
        (newStart.isBefore(eventStart) && newEnd.isAfter(eventEnd))
      );
    });
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class or event. Please choose a different time.'),
        buttons: [
          {
            label: t('Ok'),
            onClick: () => info.revert(),
          }
        ]
      });
      return;
    }
  
    confirmAlert({
      title: t('Confirm to submit'),
      message: t('Are you sure you want to resize the event?'),
      buttons: [
        {
          label: t('yes'),
          onClick: () => confirmEventChange(info),
        },
        {
          label: t('no'),
          onClick: () => info.revert(),
        },
      ],
    });
  };
  
  const confirmEventChange = async (info) => {
    let taskId = info.event.id;
    taskId = taskId.replace(/^task-/, '').replace(/^class-/, '');
  
    const newStart = info.event.start;
    const newEnd = info.event.end;
    const formattedStart = moment(newStart).format('YYYY-MM-DD HH:mm:ss');
    const formattedEnd = newEnd ? moment(newEnd).format('YYYY-MM-DD HH:mm:ss') : null;
  
    const taskToEdit = tasks.find(task => task.id === `task-${taskId}`);
    if (!taskToEdit) {
      console.error('Task not found');
      info.revert();
      return;
    }
  
    const scheduleDay = moment(newStart).format('dddd');
  
    const taskUpdateData = {
      title: taskToEdit.title,
      description: taskToEdit.description,
      start: formattedStart,
      end: formattedEnd,
      schedule_day: scheduleDay,
    };
  
    try {
      await updateTask(taskId, taskUpdateData);
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task', error);
      info.revert();
    }
  };
  
  const handleTasksChanged = () => {
    fetchTasks();
    setIsTaskPopupOpen(false);
  };

  // *** USE YOUR BEAUTIFUL EVENTRENDERER ***
  const renderEventContent = (eventInfo) => {
    return <EventRenderer eventInfo={eventInfo} />;
  };

  const events = [...tasks, ...classes];

  const startOfWeek = moment().startOf('week');
  const endOfWeek = moment().endOf('week');
  const weekTasks = tasks.filter(task =>
    moment(task.start).isBetween(startOfWeek, endOfWeek, null, '[]')
  );

  weekTasks.sort((a, b) => moment(a.start) - moment(b.start));

  const combinedTasksAndClasses = classes
    .filter(item => moment(item.start).isSameOrAfter(moment()))
    .sort((a, b) => moment(a.start) - moment(b.start));

  const upcomingTasksAndClasses = combinedTasksAndClasses.slice(0, 5);

  return (
    <div className="calendar-layout">
      <div className="calendar-container">
        <div className="calendar-border">
          <FullCalendar
            contentHeight="auto"
            editable={true}
            droppable={true}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            initialView="dayGridMonth"
            selectable={true}
            locale={i18n.language === 'th' ? thLocale : 'en'}
            selectMirror={true}
            select={handleDateClick}
            // *** SIMPLIFIED: Use events directly since they have editable properties ***
            events={events}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventResizableFromStart={true}
            eventDurationEditable={true}
            allDaySlot={false}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            slotMinTime="09:00:00"
            slotMaxTime="20:00:00"
            slotDuration="00:15:00"
            slotLabelInterval="00:30"
            scrollTime="09:00:00" 
            /* Month view renders timed events as a dot + time by default;
               AllClasses shows full blocks, which is what the card needs. */
            eventDisplay="block"
            eventContent={renderEventContent}
            eventConstraint={{
              start: '00:00',
              end: '23:59:59',
            }}
            eventClassNames={(info) => {
              if (info.event.extendedProps?.shared) {
                return ['shared-event'];
              } else if (info.event.extendedProps?.type === 'class' || info.event.id.startsWith('class-')) {
                return ['class-event'];
              } else {
                return ['task-event'];
              }
            }}
          />
        </div>
      </div>      
      {isTaskPopupOpen && (
        <TaskPopup
          isOpen={isTaskPopupOpen}
          onClose={() => setIsTaskPopupOpen(false)}
          onSave={handleTasksChanged}
          task={selectedTask}
          date={dateSelected}
          tasks={tasks}
          classes={classes}
          users={users}
        />      
      )}
    </div>
  );
};

export default Calendar;